import { useState, useMemo, useRef } from 'react';
import { useClickOutside } from '@mantine/hooks';
import {
  Box,
  Checkbox,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'react-feather';
import type { Session } from '../../../types';
import classes from './SessionMultiSelect.module.css';
import {
  allIdsSelected,
  applySelectAll,
  compareGroupNames,
  groupLabel,
  isLinkableSession,
  removeSessionIds,
  someIdsSelected,
} from './sessionSelection';

// Color mapping for groups (matching theme colors)
const GROUP_COLORS = ['groupAlpha', 'groupBeta', 'groupGamma', 'medleyGreen', 'surveyPurple'];

// Border colors for group sections (shade 5 of each theme)
const GROUP_BORDER_COLORS: Record<string, string> = {
  groupAlpha: '#8B9B6E',
  groupBeta: '#6E8B9E',
  groupGamma: '#C67A3C',
  medleyGreen: '#4A7C59',
  surveyPurple: '#8E44AD',
};

interface SessionMultiSelectProps {
  sessions: Session[];
  value: string[]; // Array of selected session IDs
  onChange: (sessionIds: string[]) => void;
  isGeneral: boolean; // Whether "General" is selected (no specific sessions)
  onGeneralChange: (isGeneral: boolean) => void;
  label?: string;
  placeholder?: string;
}

interface GroupedSessions {
  allGroups: Session[];
  workshops: Session[];
  byGroup: Map<string, { groupId: string; groupName: string; sessions: Session[] }>;
}

export function SessionMultiSelect({
  sessions: allSessions,
  value,
  onChange,
  isGeneral,
  onGeneralChange,
  label = 'Session Association',
  placeholder = 'General (no specific session)',
}: SessionMultiSelectProps) {
  const [opened, setOpened] = useState(false);
  // MED-1400: the in-form list closes on a click outside or on Escape, as the old popover did.
  const rootRef = useClickOutside<HTMLDivElement>(() => setOpened(false));
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Mantine's Modal ignores Escape from elements with this attribute, so Escape closes the list only.
  const stopModalEscape = opened ? { 'data-mantine-stop-propagation': true } : {};
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // MED-1400: callers pass different lists; filter here so every picker offers the same sessions.
  const sessions = useMemo(() => allSessions.filter(isLinkableSession), [allSessions]);

  // Group sessions by group_id, separating workshops
  const groupedSessions = useMemo((): GroupedSessions => {
    const allGroups: Session[] = [];
    const workshops: Session[] = [];
    const byGroup = new Map<string, { groupId: string; groupName: string; sessions: Session[] }>();

    // Sort sessions by session_number
    const sortedSessions = [...sessions].sort((a, b) => a.session_number - b.session_number);

    for (const session of sortedSessions) {
      if (session.session_type === 'workshop_session') {
        // Workshop sessions go to their own section
        workshops.push(session);
      } else if (!session.group_id) {
        // All groups session (group_id is null or undefined)
        allGroups.push(session);
      } else {
        // Group-specific session
        const existing = byGroup.get(session.group_id);
        if (existing) {
          existing.sessions.push(session);
        } else {
          byGroup.set(session.group_id, {
            groupId: session.group_id,
            groupName: groupLabel(session.group_name || `Group ${session.group_id.slice(0, 4)}`),
            sessions: [session],
          });
        }
      }
    }

    // MED-1400: one group order in every picker, by group name or number.
    const sortedByGroup = new Map(
      [...byGroup].sort(([, a], [, b]) => compareGroupNames(a.groupName, b.groupName)),
    );

    return { allGroups, workshops, byGroup: sortedByGroup };
  }, [sessions]);

  // Build group color mapping (consistent colors per group)
  const groupColors = useMemo(() => {
    const colors = new Map<string, string>();
    let colorIndex = 0;
    for (const groupId of groupedSessions.byGroup.keys()) {
      colors.set(groupId, GROUP_COLORS[colorIndex % GROUP_COLORS.length]);
      colorIndex++;
    }
    return colors;
  }, [groupedSessions.byGroup]);

  // Handle session checkbox change
  const handleSessionChange = (sessionId: string, checked: boolean) => {
    if (checked) {
      // If selecting a session, uncheck "General"
      if (isGeneral) {
        onGeneralChange(false);
      }
      onChange([...value, sessionId]);
    } else {
      const newValue = value.filter((id) => id !== sessionId);
      onChange(newValue);
      // If no sessions selected, default to "General"
      if (newValue.length === 0) {
        onGeneralChange(true);
      }
    }
  };

  // Handle "General" checkbox change
  const handleGeneralChange = (checked: boolean) => {
    if (checked) {
      // MED-1400: clear the sessions in the list only; "Other linked sessions" has its own row.
      onChange(hiddenIds);
      onGeneralChange(hiddenIds.length === 0);
      return;
    }
    onGeneralChange(false);
  };

  // MED-1400: every session id in the list, for the "All sessions" option.
  const allSessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);

  // MED-1400: linked ids that are not in the list (for example a 1:1 session). They stay
  // linked and get their own row, so no save drops a link the user cannot see.
  const hiddenIds = useMemo(() => removeSessionIds(value, allSessionIds), [value, allSessionIds]);

  // MED-1400: check or clear a whole set of sessions in one click.
  const handleSelectAll = (ids: string[], checked: boolean) => {
    const next = applySelectAll(value, ids, checked);
    onGeneralChange(next.isGeneral);
    onChange(next.sessionIds);
  };

  // MED-1400: a section header with a visible "Select all" checkbox for every session in it.
  const renderSectionHeader = (
    title: string,
    ids: string[],
    isExpanded: boolean,
    onToggle: () => void,
  ) => (
    <Group wrap="nowrap" gap={0} className={classes.sectionHeaderRow}>
      <Box className={classes.sectionSelectAll}>
        <Checkbox
          {...stopModalEscape}
          size="sm"
          label="Select all"
          aria-label={`Select all sessions in ${title}`}
          checked={allIdsSelected(value, ids)}
          indeterminate={someIdsSelected(value, ids) && !allIdsSelected(value, ids)}
          onChange={(e) => handleSelectAll(ids, e.currentTarget.checked)}
          classNames={{ label: classes.selectAllLabel }}
        />
      </Box>
      <UnstyledButton
        {...stopModalEscape}
        className={classes.sectionHeader}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} truncate>
            {`${title} (${ids.length})`.toUpperCase()}
          </Text>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Group>
      </UnstyledButton>
    </Group>
  );

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Build display text for the dropdown
  const displayText = useMemo(() => {
    if (isGeneral || value.length === 0) {
      return placeholder;
    }

    const otherText =
      hiddenIds.length > 0
        ? `${hiddenIds.length} other linked session${hiddenIds.length === 1 ? '' : 's'}`
        : '';
    const withOther = (text: string) => (otherText ? `${text} + ${otherText}` : text);

    const selectedSessions = sessions.filter((s) => value.includes(s.id));
    if (selectedSessions.length === 0) {
      return otherText || placeholder;
    }

    // MED-1400: say "All sessions" instead of a long "+N more" list.
    if (allSessionIds.length > 1 && allIdsSelected(value, allSessionIds)) {
      return withOther(`All sessions (${allSessionIds.length})`);
    }

    // Format: "All Groups - Session 2, Group 4 - Session 3, Intro Workshop"
    const labels = selectedSessions.map((s) => {
      if (s.session_type === 'workshop_session') {
        return s.title || `Workshop ${s.session_number}`;
      }
      if (!s.group_id) {
        return `All Groups - Session ${s.session_number}`;
      }
      return `${groupLabel(s.group_name || 'Group')} - Session ${s.session_number}`;
    });

    if (labels.length <= 2) {
      return withOther(labels.join(', '));
    }
    return withOther(`${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`);
  }, [isGeneral, value, sessions, placeholder, allSessionIds, hiddenIds]);

  return (
    <Box
      ref={rootRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && opened) {
          setOpened(false);
          triggerRef.current?.focus();
        }
      }}
    >
      {label && (
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
      )}
      {/* MED-1400: the list opens in the form, not as a floating layer, so it stays inside the modal. */}
      <UnstyledButton
        {...stopModalEscape}
        ref={triggerRef}
        className={classes.trigger}
        onClick={() => setOpened(!opened)}
        aria-expanded={opened}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c={isGeneral ? 'dimmed' : undefined} truncate>
            {displayText}
          </Text>
          {opened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Paper withBorder className={classes.dropdown}>
          <Stack gap={0}>
            {/* General checkbox */}
            <Box className={classes.generalRow}>
              <Checkbox
                {...stopModalEscape}
                label="General (no specific session)"
                checked={isGeneral}
                onChange={(e) => handleGeneralChange(e.currentTarget.checked)}
                classNames={{ label: classes.checkboxLabel }}
              />
            </Box>

            {/* MED-1400: link every session in the list in one click */}
            {allSessionIds.length > 1 && (
              <Box className={classes.generalRow}>
                <Checkbox
                  {...stopModalEscape}
                  label={`All sessions (${allSessionIds.length})`}
                  checked={allIdsSelected(value, allSessionIds)}
                  indeterminate={
                    someIdsSelected(value, allSessionIds) && !allIdsSelected(value, allSessionIds)
                  }
                  onChange={(e) => handleSelectAll(allSessionIds, e.currentTarget.checked)}
                  classNames={{ label: classes.checkboxLabel }}
                />
              </Box>
            )}

            {/* MED-1400: links to sessions outside the list stay visible and are removed only here */}
            {hiddenIds.length > 0 && (
              <Box className={classes.generalRow}>
                <Checkbox
                  {...stopModalEscape}
                  label={`Other linked sessions (${hiddenIds.length})`}
                  description="Not in this list, for example a 1:1 session. Uncheck to remove the link."
                  checked
                  onChange={() => {
                    const next = removeSessionIds(value, hiddenIds);
                    onChange(next);
                    onGeneralChange(next.length === 0);
                  }}
                  classNames={{ label: classes.checkboxLabel }}
                />
              </Box>
            )}

            {/* All Groups Section */}
            {groupedSessions.allGroups.length > 0 && (
              <Box className={classes.section}>
                {renderSectionHeader(
                  'All Groups',
                  groupedSessions.allGroups.map((s) => s.id),
                  allGroupsExpanded,
                  () => setAllGroupsExpanded(!allGroupsExpanded),
                )}
                <Collapse in={allGroupsExpanded}>
                  <Stack gap={0} className={classes.sessionList}>
                    {groupedSessions.allGroups.map((session) => (
                      <Box key={session.id} className={classes.sessionRow}>
                        <Checkbox
                          {...stopModalEscape}
                          label={`Session ${session.session_number}`}
                          checked={value.includes(session.id)}
                          onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                          classNames={{ label: classes.checkboxLabel }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}

            {/* Per-Group Sections */}
            {Array.from(groupedSessions.byGroup.entries()).map(([groupId, group]) => {
              const colorName = groupColors.get(groupId) || 'gray';
              const borderColor = GROUP_BORDER_COLORS[colorName] || '#888';
              const isExpanded = expandedGroups.has(groupId);

              return (
                <Box
                  key={groupId}
                  className={classes.section}
                  style={{ borderLeftColor: borderColor }}
                >
                  {renderSectionHeader(
                    group.groupName,
                    group.sessions.map((s) => s.id),
                    isExpanded,
                    () => toggleGroup(groupId),
                  )}
                  <Collapse in={isExpanded}>
                    <Stack gap={0} className={classes.sessionList}>
                      {group.sessions.map((session) => (
                        <Box key={session.id} className={classes.sessionRow}>
                          <Checkbox
                            {...stopModalEscape}
                            label={`Session ${session.session_number}`}
                            checked={value.includes(session.id)}
                            onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                            classNames={{ label: classes.checkboxLabel }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}

            {/* Workshops Section */}
            {groupedSessions.workshops.length > 0 && (
              <Box className={classes.section} style={{ borderLeftColor: '#8E44AD' }}>
                {renderSectionHeader(
                  'Workshops',
                  groupedSessions.workshops.map((s) => s.id),
                  expandedGroups.has('__workshops__'),
                  () => toggleGroup('__workshops__'),
                )}
                <Collapse in={expandedGroups.has('__workshops__')}>
                  <Stack gap={0} className={classes.sessionList}>
                    {groupedSessions.workshops.map((session) => (
                      <Box key={session.id} className={classes.sessionRow}>
                        <Checkbox
                          {...stopModalEscape}
                          label={session.title || `Workshop ${session.session_number}`}
                          checked={value.includes(session.id)}
                          onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                          classNames={{ label: classes.checkboxLabel }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
}
